import { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Box, TextField } from "@mui/material";
import BranchTable from "./BranchTable";
import {
  addButtonPadding,
  registerPatientButtonStyles,
} from "../../common/commonStyles";
import Grid from "@mui/material/Grid";
import BranchCreationForm from "./BranchCreationForm";
import "../../common/Common.css";
import ReusableSnackbar from "../../shared/Alert/ReusableSnackbar";
import validators from "../../utils/validations/validators";
import api from "../../services/api";

const BranchComponent = () => {
  const [open, setOpen] = useState(false);
  const [isEditClicked, setIsEditClicked] = useState(false);
  const [branchDetails, setBranchDetails] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState({
    status: false,
    message: "",
    severity: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    city: "",
    country: "",
    postal_code: "",
    contact_number: "",
    start_time: null,
    close_time: null,
    break_start_time: null,
    break_end_time: null,
  });
  const handleOpen = () => setOpen(true);
  const handleClose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    if (reason === "escapeKeyDown") {
      return;
    }
    setFormData({
      name: "",
      address: "",
      city: "",
      country: "",
      postal_code: "",
      contact_number: "",
      start_time: null,
      close_time: null,
      break_start_time: null,
      break_end_time: null,
    });
    setValidationErrors({});
    setOpen(false);
  };
  const handleSnackClose = () => setSnackbarOpen(false);
  let accessToken = localStorage.getItem("access_token");

  const getBranchDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`app/management/branches/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });
      setBranchDetails(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.log("Error fetching branches:", error);
      setBranchDetails([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    getBranchDetails();
  }, [getBranchDetails]);

  const isFormValid =
    Object.values(validationErrors).every((err) => err === "") &&
    Object.values(formData).every((val) => val !== "" && val !== null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    // Validate live
    if (validators[name]) {
      const errorMsg = validators[name](value);
      setValidationErrors((prev) => ({
        ...prev,
        [name]: errorMsg,
      }));
    }
  };

  const handleCreateBranch = async () => {
    if (!isFormValid) {
      setSnackbarOpen({
        status: true,
        message: "Please fill all the required fields correctly.",
        severity: "error",
      });
      return;
    }
    if (isEditClicked) {
      sendBranchEdit();
    } else {
      const payload = {
        ...formData,
        start_time: formData.start_time,
        close_time: formData.close_time,
        break_start_time: formData.break_start_time,
        break_end_time: formData.break_end_time,
      };
      try {
        const response = await api.post(`app/management/branches/`, payload);

        if (response.status === 200) {
          setFormData({
            name: "",
            address: "",
            city: "",
            country: "",
            postal_code: "",
            contact_number: "",
            start_time: null,
            close_time: null,
            break_start_time: null,
            break_end_time: null,
          });
          setSnackbarOpen({
            status: true,
            message: "Branch created successfully!",
          });
          handleClose();
          getBranchDetails();
        }
      } catch (error) {
        setSnackbarOpen({
          status: true,
          message: "Something went wrong ! Please try again",
          severity: "error",
        });
      }
    }
  };

  const handleBranchtEdit = async (id) => {
    setBranchId(id);
    setIsEditClicked(true);
    await getDetailsBranchWise(id);
    handleOpen();
  };

  const getDetailsBranchWise = async (id) => {
    let branchIdData = await api.get(
      `app/management/branches/?branch_id=${id}`
    );
    {
      const updatedData = {
        name: branchIdData?.data[0]?.name,
        address: branchIdData?.data[0]?.address,
        city: branchIdData?.data[0]?.city,
        country: branchIdData?.data[0]?.country,
        postal_code: branchIdData?.data[0]?.postal_code,
        contact_number: branchIdData?.data[0]?.contact_number,
        start_time: branchIdData?.data[0]?.start_time,
        close_time: branchIdData?.data[0]?.close_time,
        break_start_time: branchIdData?.data[0]?.break_start_time,
        break_end_time: branchIdData?.data[0]?.break_end_time,
      };

      setFormData(updatedData);
    }
  };
  const sendBranchEdit = async () => {
    setIsEditClicked(true);
    const payload = {
      ...formData,
      start_time: formData.start_time,
      close_time: formData.close_time,
      break_start_time: formData.break_start_time,
      break_end_time: formData.break_end_time,
    };
    try {
      let updatedPatientData = await api.patch(
        `app/management/branches/${branchId}`,
        payload
      );
      if (updatedPatientData.status === 200) {
        setFormData({
          name: "",
          address: "",
          city: "",
          country: "",
          postal_code: "",
          contact_number: "",
          start_time: null,
          close_time: null,
          break_start_time: null,
          break_end_time: null,
        });
        setSnackbarOpen({
          status: true,
          message: "Branch Details Edited successfully !",
          severity: "success",
        });
        handleClose();
        getBranchDetails();
      }
    } catch (error) {
      setSnackbarOpen({
        message: error?.response?.data?.detail || "An error occurred",
        severity: "error",
      });
    }
  };
  // 🔍 Filter rows based on search
  const filteredBranches = useMemo(() => {
    if (!searchQuery) return branchDetails;
    return branchDetails.filter((row) =>
      Object.values(row)
        .join(" ")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  }, [branchDetails, searchQuery]);

  return (
    <>
      <Box className="tabcontent_box tabcontent_box_branch">
        <Grid
          container
          spacing={2}
          className="patientTable-btnstack"
          display="flex"
          alignItems="center"
        >
          <Grid size={8}>
            <Button
              variant="contained"
              sx={[registerPatientButtonStyles, addButtonPadding]}
              size="small"
              onClick={handleOpen}
            >
              Add New Branch
            </Button>
          </Grid>
          <Grid size={4} sx={{ textAlign: "right" }}>
            <TextField
              size="small"
              placeholder="Search ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
        </Grid>

        {loading ? (
          <div style={{ textAlign: "center", marginTop: "1rem" }}>
            Loading branches, please wait...
          </div>
        ) : (
          <BranchTable
            branchDetails={filteredBranches}
            handleBranchtEdit={handleBranchtEdit}
          />
        )}

        <BranchCreationForm
          open={open}
          handleOpen={handleOpen}
          handleClose={handleClose}
          formData={formData}
          setFormData={setFormData}
          handleInputChange={handleInputChange}
          handleCreateBranch={handleCreateBranch}
          isFormValid={isFormValid}
          isEditClicked={isEditClicked}
          validationErrors={validationErrors}
        />
      </Box>
      <ReusableSnackbar
        open={snackbarOpen.status}
        message={snackbarOpen.message}
        onClose={handleSnackClose}
        severity={snackbarOpen.severity}
      />
    </>
  );
};

export default BranchComponent;
